import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap font-display font-bold tracking-wide ring-offset-background transition-all duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-gradient-to-b from-primary via-primary to-primary/80 text-primary-foreground border border-primary/40 shadow-[inset_0_1px_0_hsl(var(--primary-foreground)/0.35),inset_0_-2px_0_hsl(0_0%_0%/0.25),0_10px_24px_-8px_hsl(var(--primary)/0.55),0_2px_0_hsl(0_0%_0%/0.2)] hover:shadow-[inset_0_1px_0_hsl(var(--primary-foreground)/0.45),inset_0_-2px_0_hsl(0_0%_0%/0.3),0_14px_30px_-8px_hsl(var(--primary)/0.65),0_3px_0_hsl(0_0%_0%/0.22)] hover:-translate-y-0.5 active:translate-y-0 active:shadow-[inset_0_2px_4px_hsl(0_0%_0%/0.3)]",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "bg-gradient-to-b from-background via-background to-muted text-foreground border border-primary/30 shadow-[inset_0_1px_0_hsl(var(--foreground)/0.06),inset_0_-2px_0_hsl(var(--primary)/0.18),0_8px_20px_-8px_hsl(var(--primary)/0.35),0_2px_0_hsl(var(--primary)/0.15)] hover:shadow-[inset_0_1px_0_hsl(var(--foreground)/0.08),inset_0_-2px_0_hsl(var(--primary)/0.25),0_12px_26px_-8px_hsl(var(--primary)/0.45),0_3px_0_hsl(var(--primary)/0.18)] hover:-translate-y-0.5 active:translate-y-0 active:shadow-[inset_0_2px_4px_hsl(var(--primary)/0.2)]",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-muted hover:text-foreground",
        link: "text-accent underline-offset-4 hover:underline",
        gold:
          "bg-gradient-to-b from-accent via-accent to-accent/75 text-accent-foreground border border-accent/50 shadow-[inset_0_1px_0_hsl(var(--accent-foreground)/0.4),inset_0_-2px_0_hsl(0_0%_0%/0.25),0_10px_26px_-8px_hsl(var(--accent)/0.6),0_2px_0_hsl(0_0%_0%/0.2)] hover:shadow-[inset_0_1px_0_hsl(var(--accent-foreground)/0.5),inset_0_-2px_0_hsl(0_0%_0%/0.3),0_14px_32px_-8px_hsl(var(--accent)/0.7),0_3px_0_hsl(0_0%_0%/0.22)] hover:-translate-y-0.5 active:translate-y-0 active:shadow-[inset_0_2px_4px_hsl(0_0%_0%/0.3)]",
        goldOutline:
          "bg-gradient-to-b from-background via-background to-muted text-accent border border-accent/50 shadow-[inset_0_1px_0_hsl(var(--accent)/0.15),inset_0_-2px_0_hsl(var(--accent)/0.25),0_8px_20px_-8px_hsl(var(--accent)/0.4),0_2px_0_hsl(var(--accent)/0.2)] hover:shadow-[inset_0_1px_0_hsl(var(--accent)/0.2),inset_0_-2px_0_hsl(var(--accent)/0.35),0_12px_26px_-8px_hsl(var(--accent)/0.55),0_3px_0_hsl(var(--accent)/0.25)] hover:-translate-y-0.5 active:translate-y-0 active:shadow-[inset_0_2px_4px_hsl(var(--accent)/0.2)]",
        elegant: "bg-card border border-accent/30 text-foreground hover:border-accent/60 hover:shadow-lg shadow-accent/10 transition-all duration-300",
      },
      size: {
        default: "h-10 px-5 py-2 rounded-xl text-sm",
        sm: "h-9 rounded-lg px-4 text-sm",
        lg: "h-12 px-8 rounded-2xl text-base",
        xl: "h-14 px-10 rounded-2xl text-base md:text-lg",
        icon: "h-10 w-10 rounded-xl",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
